
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Download, X, FileImage, FileText } from "lucide-react";

interface ProgramsPageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalPrograms: number;
  onDownload: (format: 'jpg' | 'pdf', startPage: number, endPage: number) => void;
}

const ProgramsPageSelectionDialog = ({ 
  isOpen, 
  onClose, 
  totalPrograms,
  onDownload
}: ProgramsPageSelectionDialogProps) => {
  const programsPerPage = 18; // Updated to match printer settings
  const totalPages = Math.ceil(totalPrograms / programsPerPage);
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(totalPages);
  const [selectedFormat, setSelectedFormat] = useState<'jpg' | 'pdf'>('jpg');

  const handleDownload = () => {
    const validStartPage = Math.max(1, Math.min(startPage, totalPages));
    const validEndPage = Math.max(validStartPage, Math.min(endPage, totalPages));
    
    onDownload(selectedFormat, validStartPage, validEndPage);
    onClose();
  };

  const isValidRange = startPage >= 1 && endPage <= totalPages && startPage <= endPage;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Download Programs Catalog
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Select page range and format for programs catalog ({totalPrograms} programs, {totalPages} pages, {programsPerPage} programs per page)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startPage">Start Page</Label>
                <Input
                  id="startPage"
                  type="number"
                  min={1}
                  max={totalPages}
                  value={startPage}
                  onChange={(e) => setStartPage(parseInt(e.target.value) || 1)}
                />
              </div>
              <div>
                <Label htmlFor="endPage">End Page</Label>
                <Input
                  id="endPage"
                  type="number"
                  min={1}
                  max={totalPages}
                  value={endPage}
                  onChange={(e) => setEndPage(parseInt(e.target.value) || totalPages)}
                />
              </div>
            </div>
            {!isValidRange && (
              <p className="text-sm text-destructive">
                Please enter a valid page range (1-{totalPages})
              </p>
            )}
          </div>

          <div className="space-y-3">
            <Label>Download Format</Label>
            <RadioGroup value={selectedFormat} onValueChange={(value) => setSelectedFormat(value as 'jpg' | 'pdf')}>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <RadioGroupItem value="jpg" id="format-jpg" />
                <div className="flex items-center space-x-3 flex-1">
                  <FileImage className="h-5 w-5 text-blue-600" />
                  <div>
                    <Label htmlFor="format-jpg" className="text-base font-medium cursor-pointer">
                      JPG Image
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      High-quality image format, ultra-fast download
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <RadioGroupItem value="pdf" id="format-pdf" />
                <div className="flex items-center space-x-3 flex-1">
                  <FileText className="h-5 w-5 text-red-600" />
                  <div>
                    <Label htmlFor="format-pdf" className="text-base font-medium cursor-pointer">
                      PDF Document
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Professional document format, perfect for printing
                    </p>
                  </div>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleDownload} disabled={!isValidRange}>
              <Download className="w-4 h-4 mr-2" />
              Download {selectedFormat.toUpperCase()}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProgramsPageSelectionDialog;
